import React, { useState } from 'react'
import orderBy from 'lodash/orderBy'
import capitalize from 'lodash/capitalize'
import format from 'date-fns/format'
import { Accordion, AccordionSummary, AccordionDetails } from '@material-ui/core'
import ExpandMoreIcon from '@material-ui/icons/ExpandMore'
import Typography from '@material-ui/core/Typography'
import Table from '@material-ui/core/Table'
import TableBody from '@material-ui/core/TableBody'
import TableCell from '@material-ui/core/TableCell'
import TableContainer from '@material-ui/core/TableContainer'
import TableRow from '@material-ui/core/TableRow'
import TableHead from '@material-ui/core/TableHead'
import TableSortLabel from '@material-ui/core/TableSortLabel'
import { numberWithCommas } from '../../../../utils'
import { useStyles } from './styles'

const PROGRESS = {
  onTrack: 'On Track',
  behind: 'Behind',
  ahead: 'Ahead'
}

const QUARTILES = [
  {
    key: 'q1',
    value: 'Quartile 1',
    color: '#8520C7'
  },
  {
    key: 'q2',
    value: 'Quartile 2',
    color: '#EA882D'
  },
  {
    key: 'q3',
    value: 'Quartile 3',
    color: '#20B3C7'
  },
  {
    key: 'q4',
    value: 'Quartile 4',
    color: '#4B8DEA'
  }
]

const headCells = [
  { id: 'name', disablePadding: true, label: 'Name', width: '20%' },
  { id: 'wave', disablePadding: false, label: 'Wave', width: '20%' },
  { id: 'date', disablePadding: false, label: 'Start Date', width: '15%' },
  { id: 'step', disablePadding: false, label: 'Step', width: '5%' },
  { id: 'daysPerStep', disablePadding: false, label: 'D/S', width: '10%' },
  { id: 'pace', disablePadding: false, label: 'Pace', width: '10%' },
  { id: 'quartile', disablePadding: false, label: 'Quartile', width: '10%' },
  { id: 'status', disablePadding: false, label: 'Status', width: '10%' }
]

const EnhancedTableHead = ({ classes, order, orderByField, rowCount, onRequestSort }) => {
  const createSortHandler = property => event => {
    onRequestSort(event, property)
  }

  return (
    <TableHead>
      <TableRow className={classes.headerRow}>
        {headCells.map(headCell => (
          <TableCell
            style={{ width: headCell.width }}
            key={headCell.id}
            align='left'
            padding='none'
            sortDirection={orderByField === headCell.id ? order : false}
          >
            <TableSortLabel
              active={orderByField === headCell.id}
              direction={orderByField === headCell.id ? order : 'asc'}
              onClick={createSortHandler(headCell.id)}
            >
              {headCell.label}
            </TableSortLabel>
          </TableCell>
        ))}
      </TableRow>
    </TableHead>
  )
}

const EngagementUsersGrid = ({ data }) => {
  const classes = useStyles()
  const [expandedRows, setExpandedRows] = useState([])
  const [sorting, setSorting] = useState({
    q1: {
      order: 'asc',
      by: 'name'
    },
    q2: {
      order: 'asc',
      by: 'name'
    },
    q3: {
      order: 'asc',
      by: 'name'
    },
    q4: {
      order: 'asc',
      by: 'name'
    }
  })

  const handleRequestSort = key => (event, property) => {
    const isAsc = sorting[key].by === property && sorting[key].order === 'asc'
    setSorting({
      ...sorting,
      [key]: {
        order: isAsc ? 'desc' : 'asc',
        by: property
      }
    })
  }

  const handleExpand = (panelId: string) => () => {
    if (expandedRows.includes(panelId)) {
      setExpandedRows([...expandedRows.filter(id => id !== panelId)])
    } else {
      setExpandedRows([...expandedRows, panelId])
    }
  }

  const getComparator = key => user => {
    switch (sorting[key].by) {
      case 'name':
        return user.firstName ? user.firstName.toLowerCase() : ''
      case 'wave':
        return user.wave ? user.wave.name.toLowerCase() : ''
      case 'date':
        return user.wave ? user.wave.startDate : ''
      case 'step':
        return user.progress ? user.progress.currentStep : ''
      case 'daysPerStep':
        return user.progress ? user.progress.daysPerStep : ''
      case 'pace':
        return user.progress ? user.progress.paceProgress.toLowerCase() : ''
      case 'quartile':
        return user.quartile ? user.quartile : 0
      case 'status':
        return user.status ? user.status.toLowerCase() : ''
      default:
        return user.firstName ? user.firstName.toLowerCase() : ''
    }
  }

  const userData = {
    q1: data.filter(user => user.quartile === 1),
    q2: data.filter(user => user.quartile === 2),
    q3: data.filter(user => user.quartile === 3),
    q4: data.filter(user => user.quartile === 4)
  }

  const renderQuartileTable = key => {
    const data = orderBy(userData[key], getComparator(key), sorting[key].order)

    return (
      <TableContainer>
        <Table className={classes.table} size='small'>
          <EnhancedTableHead
            classes={classes}
            order={sorting[key].order}
            orderByField={sorting[key].by}
            onRequestSort={handleRequestSort(key)}
            rowCount={data.length}
          />
          <TableBody>
            {data.map((user, index) => (
              <TableRow className={classes.tableRow} hover tabIndex={-1} key={user.id}>
                <TableCell align='left' padding='none'>
                  {`${user.firstName} ${user.lastName}`}
                </TableCell>
                <TableCell align='left'>{user.wave ? user.wave.name : ''}</TableCell>
                <TableCell align='left'>{user.wave ? format(user.wave.startDate, 'MM/dd/yy') : ''}</TableCell>
                <TableCell align='left'>{user.progress ? user.progress.currentStep : ''}</TableCell>
                <TableCell align='left'>
                  {user.progress ? numberWithCommas(Math.round((user.progress.daysPerStep + Number.EPSILON) * 10) / 10) : ''}
                </TableCell>
                <TableCell
                  align='left'
                  style={user.progress ? { color: user.progress.paceProgress === 'behind' ? '#FFD000' : '#05C34B', fontWeight: '500' } : {}}
                >
                  {user.progress ? PROGRESS[user.progress.paceProgress] || '' : ''}
                </TableCell>
                <TableCell align='left'>{user.quartile}</TableCell>
                <TableCell
                  align='left'
                  style={user.status ? { color: user.status === 'nonStarter' ? '#FFD000' : '#05C34B', fontWeight: '500' } : {}}
                >
                  {capitalize(user.status)}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    )
  }

  return (
    <div className={classes.container}>
      <div className={classes.root}>
        {QUARTILES.map(({ key, value, color }) => (
          <Accordion key={key} expanded={expandedRows.includes(key)} onChange={handleExpand(key)}>
            <AccordionSummary className={classes.panel} style={{ cursor: 'pointer' }} expandIcon={<ExpandMoreIcon />}>
              <Typography className={classes.quartileName} style={{ color }} variant='h5'>
                {value}
              </Typography>
              <Typography className={classes.learnersCount} variant='h6'>
                {`Learners ${userData[key].length}`}
              </Typography>
            </AccordionSummary>
            <AccordionDetails className={classes.details}>{renderQuartileTable(key)}</AccordionDetails>
          </Accordion>
        ))}
      </div>
    </div>
  )
}

export default EngagementUsersGrid
